/* eslint-disable import/no-named-as-default */
import React from "react";
import { Route, Switch, withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { hot } from "react-hot-loader";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import styled, { keyframes } from "styled-components";

import { getDetails, logout, externalLogin } from "../../actions/user";
import { loadConfig, setDefaults, setParams } from '../../actions/general';
import { getSizesProfileList } from "../../actions/sizes";
import { onFabricChanged } from "../../actions/render";
import { applyPreset } from '../../actions/ui';

import BuilderPage from "../BuilderPage";
import CatalogPage from "../CatalogPage";
import ProductPage from '../ProductPage';

const LoaderRoot = styled.div`
  display: block;
`

const LoaderWrap = styled.div`
  display: block;
`;

const SvgWrap = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1500;
    transition: top 0.3s ease-in-out;
    opacity: 0.6;


    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 10;
      background: #fff;
      transition: opacity 0.7s 0.1s;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 20;
      background: #000;
      transition: top 0.3s ease-in-out;
    }

`;

const loaderAnimation = keyframes`
  0% {
    stroke-dashoffset:1024px;
  }
  50% {
    stroke-dashoffset:0;
  }
  100% {
    stroke-dashoffset:-1024px;
  }
`;


const LoaderSvg = styled.svg`
    transition: opacity 0.5s;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 30;
    max-width: 62px;
    margin: auto;
    fill: none;
    stroke: #fff6;
    stroke-width: 4px;
    stroke-dashoffset: 1024px;
    stroke-dasharray: 1024px;
    animation-name: ${loaderAnimation};
    animation-duration: 2s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-timing-function: ease-out;
`;


const RemarkSvg = () => (
  <LoaderSvg
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    x="0px"
    y="0px"
    viewBox="0 0 210 297"
  >
    <path
      d="M76.0067 209.607l33.3792 0 26.7034 67.7121 61.0363 0 -28.6108 -71.5269c16.2128,-6.6758 27.6571,-22.8886 27.6571,-41.9624l0 -96.3229c0,-24.796 -20.0275,-45.7772 -45.7772,-45.7772l-74.388 0 -3.8147 0 -53.4068 0 0 254.636 57.2215 0 0 -66.7584zm0 -127.795l55.3142 0c5.7221,0 9.5369,3.8147 9.5369,9.5369l0 48.6383c0,5.7221 -3.8148,9.5369 -9.5369,9.5369l-55.3142 0 0 -67.7121z">
    </path>
  </LoaderSvg>
);

const RemarkLoader = () => (
  <LoaderRoot>
    <LoaderWrap>
      <SvgWrap>
        <RemarkSvg />
      </SvgWrap>
    </LoaderWrap>
  </LoaderRoot>
)

class App extends React.PureComponent {

  constructor(props) {
    super(props);
    this.state = {
      lang: 'ru',
      configLoaded: false,
      fabric: '',
      preset: '',
    }
    this.rootRef = React.createRef();
    this.onLogout.bind(this);
  }

  async componentDidMount() {
    if (!this.state.configLoaded) {
      const { actions: {
          getDetails, 
          setDefaults, 
          setParams, 
          onFabricChanged, 
          applyPreset 
      }} = this.props;

      await loadConfig();

      await getDetails();
      await setDefaults();

      const search = window.location.search;
      const params = new URLSearchParams(search);
      const shirtId = params.get('shirt_id');
      if (shirtId) {
        await setParams(shirtId);
      }

      const fabric = params.get('fabric');
      const preset = params.get('preset');
      if(fabric && preset) {
        onFabricChanged(fabric);
        await applyPreset(preset);
      }

      this.setState({
        configLoaded: true,
        fabric, preset,
      });
    }

    document.addEventListener('logoutEvent', this.onLogout)
    document.addEventListener('loginEvent', this.onLogin)
  }
  
  componentDidUpdate = async (prevProps, prevState) => {
    const { actions: { onFabricChanged, applyPreset }} = this.props;

    const params = new URLSearchParams(window.location.search);
    const fabric = params.get('fabric');
    const preset = params.get('preset');
    if(prevState.fabric !== fabric || prevState.preset !== preset) {
      if(fabric) {
        onFabricChanged(fabric);
      }
      if(preset) {
        await applyPreset(preset);
      }
      this.setState({ fabric, preset });
    }
  }

  onLogout = async ({ detail }) => {

    await this.props.actions.logout(detail);
    await this.props.actions.getDetails();
    await this.props.actions.getSizesProfileList();
  }


  onLogin = async ({ detail }) => {

    await this.props.actions.externalLogin(detail)
    await this.props.actions.getDetails();
    await this.props.actions.getSizesProfileList();
  }

  render() {
    const { configLoaded, fabric, preset } = this.state;

    if (!configLoaded) {
      return (
        <RemarkLoader />
      )
    }

    return (

      <div
        ref={this.rootRef}
        className="b-layout"
      >
        <Switch>
          <Route exact path="/" component={BuilderPage} />
          <Route exact path="/b_designer" component={BuilderPage} />
          <Route exact path="/b_catalog/formal">
            <CatalogPage preset={'formal'} />
          </Route>
          <Route exact path="/b_catalog/business">
            <CatalogPage preset={'business'} />
          </Route>
          <Route exact path="/b_catalog/business_casual">
            <CatalogPage preset={'business-casual'} />
          </Route>
          <Route exact path="/b_catalog/casual">
            <CatalogPage preset={'casual'} />
          </Route>
          <Route exact path="/b_catalog/tuxedo">
            <CatalogPage preset={'tuxedo'} />
          </Route>
          <Route exact path="/b_catalog">
            <CatalogPage />
          </Route>
        </Switch>
        {fabric && preset && <Route path="/b_catalog" children={<ProductPage/>} />}
      </div>

    );
  }
}

App.propTypes = {
  children: PropTypes.element,
  actions: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        getDetails,
        logout,
        externalLogin,
        setDefaults,
        getSizesProfileList,
        setParams,
        onFabricChanged,
        applyPreset,
      }, dispatch),
  };
}


export default hot(module)(withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(App)));
